import { inject, Injectable } from '@angular/core';

import { catchError, from, map, mergeMap, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { Action } from '@ngrx/store';
import { BaseEffects } from '@store/base.effects';
import { createEffect, ofType } from '@ngrx/effects';

import { CmsPageActions } from './cms-pages.actions';
import { PageMetasActions } from '../page-metas';
import { RouterActions } from '@store/router';
import { RootActions } from '@store/root.actions';

import { CmsComponentsResolver } from '@services/resolvers/cms-components-resolver';
import { HttpHelper } from '@lib/utils/helpers/http-helper/http-helper';
import { LanguagesEnum } from '@core/config';

@Injectable({ providedIn: 'root' })
export class CmsPagesEffects extends BaseEffects {
  private blankEntrypoints: string[] = Object.keys(LanguagesEnum).reduce((coll, lang) => {
    const _lang = lang.toLowerCase();
    return [...coll, `/${_lang}`, `/${_lang}/`];
  }, []);

  private cmsComponentResolver = inject(CmsComponentsResolver);

  fetch$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CmsPageActions.fetch),
      mergeMap(({ url }) => {
        url ||= HttpHelper.getLocalizedPath('');

        if (this.blankEntrypoints.includes(url.toLowerCase())) {
          return of(RouterActions.navigate({ url }));
        }
  
        return from(this.cmsComponentResolver.resolveComponents(url)).pipe(
          map(pageConfig => {
            if (pageConfig === null) {
              return RootActions.NULL_ACTION();
            }
        
            return CmsPageActions.fetchSuccess({ payload: { url, ...pageConfig } });
          }),
          catchError(error => of(CmsPageActions.fetchError({ error })))
        );
      })
    )
  );

  fetchError$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CmsPageActions.fetchError),
      tap(({ error }) => this.logError(error)),
      mergeMap(() => of(RouterActions.navigate({ url: '/site/404' })))
    )
  );

  fetchSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CmsPageActions.fetchSuccess),
      mergeMap(({ payload }) => of(PageMetasActions.buildMetas({ payload: payload.seo })))
    )
  );
}
